// Composer de notas de ticket con formato OPCIONAL. Réplica del NoteEditor
// compartido del CRM (crm/apps/web/src/shared/notes/NoteEditor.tsx): por
// defecto es el textarea sencillo de siempre (pegar capturas incluido — mismo
// pipeline de tokens que ya existía en TicketDetail); un botón discreto
// («Aa · Texto con formato») activa PARA ESA NOTA el editor enriquecido
// (negritas, listas, enlaces, emojis…). El modo no se persiste: cada nota
// nueva arranca sencilla.
//
// Modelo al guardar (toPayload): en rico se persiste bodyHtml + body (la
// derivación plana con tokens, que en el CRM alimenta snippets/búsqueda/
// historial) y las capturas data-URI del editor se EXTRAEN a adjuntos
// (extractRichNoteContent) — el data-URI nunca se persiste.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ATTACHMENT_TOKEN_SCHEME,
  inlineImageToken,
  makePastedImageFilename,
} from "../../api/crmInlineImages";
import {
  extractRichNoteContent,
  htmlHasRichFormatting,
  plainTextToNoteHtml,
} from "../../api/crmRichNote";
import {
  CreateTicketCommentBody,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS_TOTAL_BYTES,
  MAX_NOTE_BODY_CHARS,
  MAX_NOTE_HTML_CHARS,
  TicketAttachment,
} from "../../api/crmTypes";
import { ErrorBox, TextArea } from "../FormControls";
import { AttachmentPicker, fileToBase64, formatBytes } from "./attachmentPicker";
import { FetchAttachmentObjectUrl } from "./NoteBody";
import RichTextEditor from "./RichTextEditor";

export type NoteComposerState = ReturnType<typeof useNoteComposer>;

export interface UseNoteComposerOpts {
  /** Adjuntos YA persistidos (edición de una nota existente): hidratan las
   *  imágenes referenciadas por token/attachment: en el editor. */
  persistedAttachments?: TicketAttachment[];
  /** Descarga autenticada del blob (por superficie: ticket o lead); necesario
   *  para editar en modo rico una nota con imágenes persistidas. */
  fetchObjectUrl?: FetchAttachmentObjectUrl;
}

export function useNoteComposer(
  picker: AttachmentPicker,
  opts: UseNoteComposerOpts = {},
) {
  const [mode, setMode] = useState<"plain" | "rich">("plain");
  const [plain, setPlain] = useState("");
  const [richHtml, setRichHtml] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Cambio de modo EN CURSO (hidratar imágenes persistidas es asíncrono): la
  // superficie se deshabilita mientras tanto — si no, lo tecleado durante la
  // descarga se perdería (el HTML se construye del `plain` capturado al clic).
  const [switching, setSwitching] = useState(false);
  /** src efímero (data:) → filename de un adjunto YA contabilizado como
   *  pendiente, para que la extracción al guardar no lo duplique. */
  const srcMap = useRef(new Map<string, string>());
  /** blob: (objectURL) → filename de un adjunto PERSISTIDO hidratado al editar.
   *  Al guardar vuelve a `attachment:filename` sin re-subir nada (el CRM
   *  conserva los adjuntos previos en la edición). */
  const persistedSrc = useRef(new Map<string, string>());
  /** objectURLs creados al hidratar (se revocan al desmontar/reset). */
  const hydratedUrls = useRef<string[]>([]);

  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(
    () => () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      hydratedUrls.current.forEach((u) => URL.revokeObjectURL(u));
    },
    [],
  );

  /** blob URL de un adjunto persistido + registro en el mapa src→filename. */
  const hydratePersisted = useCallback(
    async (filename: string): Promise<string | null> => {
      const { persistedAttachments = [], fetchObjectUrl } = optsRef.current;
      const a = persistedAttachments.find(
        (x) => x.filename === filename && x.contentType.startsWith("image/"),
      );
      if (!a || !fetchObjectUrl) return null;
      try {
        const url = await fetchObjectUrl(a.id);
        persistedSrc.current.set(url, filename);
        hydratedUrls.current.push(url);
        return url;
      } catch {
        return null;
      }
    },
    [],
  );

  /** Resuelve un src del editor a un adjunto que NO hay que re-subir: los
   *  persistidos hidratados (blob:) siempre valen; los pendientes (data:) solo
   *  si siguen vivos en el picker — si el usuario quitó el chip, el mapa queda
   *  obsoleto y la imagen (aún visible en el editor) debe re-extraerse como
   *  adjunto nuevo, o el bodyHtml persistiría un attachment: que no existe. */
  const knownSrcFilename = useCallback(
    (src: string): string | null => {
      const persisted = persistedSrc.current.get(src);
      if (persisted) return persisted;
      const fn = srcMap.current.get(src);
      if (!fn) return null;
      return picker.attachments.some((a) => a.filename === fn) ? fn : null;
    },
    [picker.attachments],
  );

  /** Carga un borrador (EDICIÓN de una nota existente). Si la nota es rica,
   *  hidrata sus `attachment:` a blob URLs ANTES de abrir el editor (await del
   *  caller); si es sencilla, abre el textarea con su body (los tokens siguen
   *  resolviendo contra persistedAttachments al volver a rico). */
  const load = useCallback(
    async (input: { body: string; bodyHtml?: string | null }) => {
      setError(null);
      picker.clear();
      srcMap.current.clear();
      persistedSrc.current.clear();
      if (!input.bodyHtml) {
        setPlain(input.body);
        setRichHtml("");
        setMode("plain");
        return;
      }
      let html = input.bodyHtml;
      const referenced = (optsRef.current.persistedAttachments ?? []).filter(
        (a) => html.includes(`src="${ATTACHMENT_TOKEN_SCHEME}${a.filename}"`),
      );
      // Fetches independientes → en paralelo (tiempo-a-editor = el más lento).
      const urls = await Promise.all(
        referenced.map((a) => hydratePersisted(a.filename)),
      );
      referenced.forEach((a, i) => {
        const url = urls[i];
        if (url) {
          html = html
            .split(`src="${ATTACHMENT_TOKEN_SCHEME}${a.filename}"`)
            .join(`src="${url}"`);
        }
      });
      setRichHtml(html);
      setPlain("");
      setMode("rich");
    },
    [picker, hydratePersisted],
  );

  /** Sencillo → rico: convierte el borrador; los tokens de adjuntos de imagen
   *  (pendientes O persistidos, si estamos editando) pasan a `<img>` para
   *  verse en el editor. */
  const switchToRich = useCallback(async () => {
    setError(null);
    setSwitching(true);
    try {
      const pendingByName = new Map(
        picker.attachments.map((a) => [a.filename, a] as const),
      );
      // Persistidos referenciados por token en el texto → blob URL (fetch).
      const referenced = (optsRef.current.persistedAttachments ?? []).filter(
        (a) => a.contentType.startsWith("image/") && plain.includes(a.filename),
      );
      const persistedByName = new Map<string, string>();
      const urls = await Promise.all(
        referenced.map((a) => hydratePersisted(a.filename)),
      );
      referenced.forEach((a, i) => {
        if (urls[i]) persistedByName.set(a.filename, urls[i]!);
      });
      const html = plainTextToNoteHtml(plain, (filename) => {
        const local = pendingByName.get(filename);
        if (local?.contentType.startsWith("image/")) {
          const src = `data:${local.contentType};base64,${local.contentBase64}`;
          srcMap.current.set(src, filename);
          return src;
        }
        return persistedByName.get(filename) ?? null;
      });
      setRichHtml(html);
      setMode("rich");
    } finally {
      setSwitching(false);
    }
  }, [plain, picker.attachments, hydratePersisted]);

  /** Rico → sencillo: deriva el texto (tokens incluidos). Las imágenes pegadas
   *  en modo rico (aún data-URI) pasan al pipeline de adjuntos pendientes para
   *  que sus tokens sigan resolviendo. Pide confirmación si hay formato real. */
  const switchToPlain = useCallback(() => {
    setError(null);
    if (
      htmlHasRichFormatting(richHtml) &&
      !window.confirm(
        "Volver a texto sencillo quitará el formato (negritas, listas, enlaces…). ¿Continuar?",
      )
    ) {
      return;
    }
    const { body, images } = extractRichNoteContent(richHtml, knownSrcFilename);
    if (images.length > 0) {
      const added = picker.addLocal(
        images.map((im) => ({
          filename: im.filename,
          contentType: im.contentType,
          contentBase64: im.contentBase64,
          size: Math.floor((im.contentBase64.length * 3) / 4),
        })),
      );
      // Límite superado: el error ya está pintado (attachError); seguimos en rico.
      if (added === null) return;
    }
    setPlain(body);
    setMode("plain");
  }, [richHtml, picker, knownSrcFilename]);

  /** Pegado de imagen en modo rico → data URL. Valida tamaño por archivo y el
   *  TOPE de nº de adjuntos YA al pegar (contando las imágenes que ya viven en
   *  el editor + los pendientes): descubrirlo solo al guardar obligaría a cazar
   *  y borrar imágenes dentro del editor. El total de bytes se valida al
   *  guardar (toPayload). */
  const richImageFilesToSrcs = useCallback(
    async (files: File[]): Promise<{ src: string; alt?: string }[]> => {
      const out: { src: string; alt?: string }[] = [];
      // Los adjuntos pendientes que YA viven como <img> en el editor (vía
      // switchToRich, registrados en srcMap) no deben contarse dos veces.
      const mapped = new Set(Array.from(srcMap.current.values()));
      let count =
        (richHtml.match(/<img\b/gi)?.length ?? 0) +
        picker.attachments.filter((a) => !mapped.has(a.filename)).length;
      for (const f of files) {
        if (count >= MAX_ATTACHMENTS) {
          setError(`Máximo ${MAX_ATTACHMENTS} adjuntos por nota`);
          break;
        }
        if (f.size > MAX_ATTACHMENT_BYTES) {
          setError(
            `${f.name || "imagen"} supera ${formatBytes(MAX_ATTACHMENT_BYTES)}`,
          );
          continue;
        }
        const base64 = await fileToBase64(f);
        out.push({
          src: `data:${f.type || "image/png"};base64,${base64}`,
          alt: "captura",
        });
        count++;
      }
      return out;
    },
    [richHtml, picker.attachments],
  );

  /** Payload para el POST, o null si algo no valida (el error queda pintado).
   *  En rico, extrae las capturas data-URI a adjuntos y valida los límites
   *  COMBINADOS (pendientes + extraídas) — el mismo juego de topes que el
   *  pipeline sencillo. */
  const toPayload = useCallback((): CreateTicketCommentBody | null => {
    setError(null);
    if (mode === "plain") {
      // El maxLength del textarea solo limita el TECLEO: la derivación
      // rico→sencillo (switchToPlain) puede colocar un body más largo.
      if (plain.trim().length > MAX_NOTE_BODY_CHARS) {
        setError("La nota es demasiado larga");
        return null;
      }
      return {
        body: plain.trim(),
        bodyHtml: null,
        attachments: picker.toBodyAttachments(),
      };
    }
    const { bodyHtml, body, images } = extractRichNoteContent(
      richHtml,
      knownSrcFilename,
    );
    const sizeOf = (b64: string) => Math.floor((b64.length * 3) / 4);
    if (picker.attachments.length + images.length > MAX_ATTACHMENTS) {
      setError(`Máximo ${MAX_ATTACHMENTS} adjuntos por nota`);
      return null;
    }
    const oversize = images.find(
      (im) => sizeOf(im.contentBase64) > MAX_ATTACHMENT_BYTES,
    );
    if (oversize) {
      setError(
        `${oversize.filename} supera ${formatBytes(MAX_ATTACHMENT_BYTES)}`,
      );
      return null;
    }
    const totalBytes =
      picker.attachments.reduce((s, a) => s + a.size, 0) +
      images.reduce((s, im) => s + sizeOf(im.contentBase64), 0);
    if (totalBytes > MAX_ATTACHMENTS_TOTAL_BYTES) {
      setError(
        `Los adjuntos superan ${formatBytes(MAX_ATTACHMENTS_TOTAL_BYTES)} en total`,
      );
      return null;
    }
    // Mismos topes que el contrato del CRM: 64K de HTML y 10K del body plano
    // derivado. Rechazar (no truncar): un slice silencioso desincronizaría el
    // par body/bodyHtml que alimenta historial de versiones y snippets.
    if (bodyHtml.length > MAX_NOTE_HTML_CHARS || body.length > MAX_NOTE_BODY_CHARS) {
      setError("La nota es demasiado larga");
      return null;
    }
    // Un doc con solo una imagen NO está vacío (una captura sola es nota válida).
    const empty = body.trim().length === 0 && !/<img\b/i.test(bodyHtml);
    return {
      body,
      bodyHtml: empty ? null : bodyHtml,
      attachments: [
        ...picker.toBodyAttachments(),
        ...images.map(({ filename, contentType, contentBase64 }) => ({
          filename,
          contentType,
          contentBase64,
        })),
      ],
    };
  }, [mode, plain, richHtml, picker, knownSrcFilename]);

  // Al EDITAR, los adjuntos ya persistidos también cuentan como contenido: el
  // CRM acepta dejar una nota solo-adjunto (vaciar el texto conservando el
  // documento); sin esto, Guardar quedaría deshabilitado para una edición
  // válida. En el composer (crear) opts está vacío y no aporta nada.
  const persistedCount = opts.persistedAttachments?.length ?? 0;
  const hasContent =
    mode === "plain"
      ? plain.trim().length > 0 ||
        picker.attachments.length > 0 ||
        persistedCount > 0
      : // &nbsp; cuenta como vacío (un contenteditable con solo un espacio
        // serializa <p>&nbsp;</p>); si no, Publicar se habilitaría para una
        // nota que toPayload derivaría a vacía.
        richHtml.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim().length > 0 ||
        /<img\b/i.test(richHtml) ||
        picker.attachments.length > 0 ||
        persistedCount > 0;

  const reset = useCallback(() => {
    setMode("plain");
    setPlain("");
    setRichHtml("");
    setError(null);
    srcMap.current.clear();
    persistedSrc.current.clear();
    hydratedUrls.current.forEach((u) => URL.revokeObjectURL(u));
    hydratedUrls.current = [];
    picker.clear();
  }, [picker]);

  return {
    mode,
    plain,
    setPlain,
    richHtml,
    setRichHtml,
    error,
    hasContent,
    switching,
    load,
    switchToRich,
    switchToPlain,
    richImageFilesToSrcs,
    toPayload,
    reset,
  };
}

/**
 * Superficie de edición de la nota: textarea sencillo (por defecto, con el
 * pegado de capturas por token) o el editor enriquecido (opt-in por nota), +
 * error + toggle. Los botones de guardar/adjuntar y los chips de adjuntos los
 * pone el caller (AttachmentPickerUI), igual que hoy.
 */
export default function NoteEditor({
  state,
  picker,
  placeholder,
  rows = 3,
  disabled,
  onSubmitShortcut,
}: {
  state: NoteComposerState;
  picker: AttachmentPicker;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  /** Ctrl/Cmd+Enter → publicar (en ambos modos). */
  onSubmitShortcut?: () => void;
}) {
  const s = state;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Inserta texto en la posición del cursor del textarea (o al final si no hay
  // ref/selección). Recoloca el cursor tras el texto insertado.
  const insertAtCursor = useCallback(
    (text: string) => {
      const ta = textareaRef.current;
      if (!ta) {
        s.setPlain(s.plain ? `${s.plain}\n${text}` : text);
        return;
      }
      const start = ta.selectionStart ?? ta.value.length;
      const end = ta.selectionEnd ?? ta.value.length;
      s.setPlain(ta.value.slice(0, start) + text + ta.value.slice(end));
      requestAnimationFrame(() => {
        const pos = start + text.length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      });
    },
    [s],
  );

  // Pegado de imágenes (capturas) en modo sencillo: cada imagen del
  // portapapeles se añade como adjunto con filename único y se inserta su
  // token inline en el body. El pegado de texto normal sigue su curso.
  const onPastePlain = useCallback(
    async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const images = Array.from(e.clipboardData.items)
        .filter((it) => it.kind === "file" && it.type.startsWith("image/"))
        .map((it) => it.getAsFile())
        .filter((f): f is File => f != null);
      if (images.length === 0) return;
      e.preventDefault();
      const named = images.map((f) => {
        const filename = makePastedImageFilename(f.type);
        return new File([f], filename, { type: f.type });
      });
      const filenames = await picker.addFiles(named);
      if (!filenames) return;
      insertAtCursor(filenames.map((fn) => inlineImageToken(fn)).join("\n"));
    },
    [picker, insertAtCursor],
  );

  return (
    <div>
      {s.mode === "plain" ? (
        <TextArea
          value={s.plain}
          onChange={s.setPlain}
          rows={rows}
          maxLength={MAX_NOTE_BODY_CHARS}
          placeholder={placeholder}
          disabled={disabled || s.switching}
          onPaste={onPastePlain}
          onKeyDown={(e) => {
            if (onSubmitShortcut && (e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              onSubmitShortcut();
            }
          }}
          textareaRef={textareaRef}
        />
      ) : (
        <RichTextEditor
          value={s.richHtml}
          onChange={s.setRichHtml}
          placeholder={placeholder}
          disabled={disabled}
          onPasteImageFiles={s.richImageFilesToSrcs}
          onSubmitShortcut={onSubmitShortcut}
        />
      )}
      {s.error && <ErrorBox>{s.error}</ErrorBox>}
      <div style={{ marginTop: "0.25rem" }}>
        <button
          type="button"
          onClick={() =>
            void (s.mode === "plain" ? s.switchToRich() : s.switchToPlain())
          }
          disabled={disabled || s.switching}
          title={
            s.mode === "plain"
              ? "Negritas, listas, enlaces, emojis… solo para esta nota"
              : undefined
          }
          style={{
            border: "none",
            background: "transparent",
            color: "#73879C",
            fontSize: ".72rem",
            padding: 0,
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {s.mode === "plain" ? "Aa · Texto con formato" : "Volver a texto sencillo"}
        </button>
      </div>
    </div>
  );
}
